export type CoverPalette = {
  base: string;
  end: string;
  glow: string;
  accent: string;
};

const paletteCache = new Map<string, Promise<CoverPalette | null>>();

function rgbToHsl(red: number, green: number, blue: number) {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  if (!delta) return { hue: 0, saturation: 0, lightness };
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  const hue =
    max === r
      ? ((g - b) / delta + (g < b ? 6 : 0)) / 6
      : max === g
        ? ((b - r) / delta + 2) / 6
        : ((r - g) / delta + 4) / 6;
  return { hue: hue * 360, saturation, lightness };
}

function hsl(hue: number, saturation: number, lightness: number) {
  return `hsl(${Math.round(hue)} ${Math.round(saturation)}% ${Math.round(lightness)}%)`;
}

async function samplePalette(source: string): Promise<CoverPalette | null> {
  if (typeof document === 'undefined') return null;
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  const loaded = new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('cover palette image failed to load'));
  });
  image.src = source;
  try {
    await Promise.race([
      loaded,
      new Promise<void>((_, reject) =>
        window.setTimeout(
          () => reject(new Error('cover palette image timed out')),
          1500,
        ),
      ),
    ]);
    const canvas = document.createElement('canvas');
    canvas.width = 72;
    canvas.height = 72;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const buckets = new Map<number, { score: number; red: number; green: number; blue: number; weight: number }>();

    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = pixels[index + 3] / 255;
      if (alpha < 0.32) continue;
      const red = pixels[index];
      const green = pixels[index + 1];
      const blue = pixels[index + 2];
      const color = rgbToHsl(red, green, blue);
      if (color.lightness < 0.045 || color.lightness > 0.94) continue;
      // Hue grouping preserves the image's largest colour family instead of
      // picking one exact shade. Saturation is only a gentle preference so a
      // small bright accessory cannot outweigh the character's main outfit.
      const key = Math.round(color.hue / 15) % 24;
      const weight = alpha * (0.7 + color.saturation * 0.65);
      const entry = buckets.get(key) ?? { score: 0, red: 0, green: 0, blue: 0, weight: 0 };
      entry.score += weight;
      entry.red += red * weight;
      entry.green += green * weight;
      entry.blue += blue * weight;
      entry.weight += weight;
      buckets.set(key, entry);
    }

    const dominant = [...buckets.values()].sort((left, right) => right.score - left.score)[0];
    if (!dominant?.weight) return null;
    const color = rgbToHsl(
      dominant.red / dominant.weight,
      dominant.green / dominant.weight,
      dominant.blue / dominant.weight,
    );
    const saturation = Math.max(20, Math.min(58, color.saturation * 100));
    return {
      base: hsl(color.hue, saturation * 0.48, 8),
      end: hsl(color.hue, saturation * 0.72, 19),
      glow: hsl(color.hue, Math.min(64, saturation * 1.08), 30),
      accent: hsl(color.hue, Math.min(72, saturation * 1.08), 68),
    };
  } catch {
    return null;
  }
}

export function coverPaletteFromImage(source?: string) {
  if (!source) return Promise.resolve(null);
  const cached = paletteCache.get(source);
  if (cached) return cached;
  const palette = samplePalette(source);
  paletteCache.set(source, palette);
  return palette;
}
